'use client';

import React, { useState } from 'react';
import { cn } from '../../lib/utils';
import Image, { type ImageLoaderProps } from 'next/image';

// Bypass @unpic/react's Supabase CDN auto-detection (which rewrites the URL
// to /storage/v1/render/image/public/...?width=N — that endpoint 403s when
// the Image Transformation add-on is disabled). Returning the src verbatim
// makes the shim render a plain <img>.
const passthroughLoader = ({ src }: ImageLoaderProps) => src;
import Link from 'next/link';
import { Icon } from '@iconify/react';
import { PlaceholderImage } from '../../components/ui/placeholder-image';
import { useFocusEffect } from '../../hooks';
import { TechnologyItem, TechnologyTagList } from './technology-tag';
import { useTranslation } from 'react-i18next';

/**
 * ProjectCard component
 * Displays a project with image, title, description, and technologies
 * Used in the projects grid section
 * Enhanced with styling consistent with the rest of the portfolio
 */
export interface ProjectCardProps {
  title: string;
  description: string;
  imageSrc: string;
  href: string;
  iconName?: string;
  technologies?: TechnologyItem[];
  className?: string;
  translationKey?: string;
}

export function ProjectCard({
  title,
  description,
  imageSrc,
  href,
  iconName,
  technologies = [],
  className,
  translationKey
}: ProjectCardProps) {
  const { t } = useTranslation();
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);

  // Use our custom focus effect hook
  const { isFocused, ref, focusProps } = useFocusEffect<HTMLDivElement>();

  // Map icon names to Iconify icon IDs
  const getIconId = (name: string) => {
    const iconMap: Record<string, string> = {
      'FaTree': 'fa6-solid:tree',
      'FaEye': 'fa6-solid:eye',
      'IoMdPulse': 'ion:pulse',
      'RiSpeakLine': 'ri:speak-line',
      'FaHeadphones': 'fa6-solid:headphones',
      'FaMicrophone': 'fa6-solid:microphone'
    };

    return iconMap[name] || 'carbon:document';
  };

  return (
    <Link href={href} className="block group h-full">
      <div
        ref={ref}
        className={cn(
          'flex flex-col h-full overflow-hidden rounded-xl border border-slate-200 dark:border-slate-700/50',
          'bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-900/80 dark:to-slate-800/80 transition-all duration-300',
          'hover:border-slate-300 dark:hover:border-slate-600 hover:translate-y-[-4px]',
          isFocused ? 'shadow-lg ring-2 ring-slate-300/50 dark:ring-slate-600/30 translate-y-[-4px]' : 'shadow-sm',
          className
        )}
        {...focusProps}
      >
        {/* Project Image with Overlay */}
        <div className="relative h-48 w-full overflow-hidden bg-slate-200/50 dark:bg-slate-800/50">
          {!imageError ? (
            <>
              {imageLoading && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-8 h-8 border-2 border-slate-300/50 dark:border-slate-600/50 border-t-slate-500 dark:border-t-slate-400 rounded-full animate-spin" />
                </div>
              )}
              <Image
                src={imageSrc}
                alt={translationKey ? t(`Projects.items.${translationKey}.title`) : title}
                fill
                className={cn(
                  "object-cover transition-transform duration-500",
                  imageLoading ? "opacity-0" : "opacity-100 transition-opacity duration-300",
                  isFocused ? "scale-105 blur-[2px]" : "scale-100"
                )}
                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                onError={() => setImageError(true)}
                onLoad={() => setImageLoading(false)}
                priority={false}
                loader={passthroughLoader}
              />
              <div className={cn(
                "absolute inset-0 bg-gradient-to-t from-slate-900/70 to-transparent transition-opacity duration-300",
                isFocused ? "opacity-90" : "opacity-80"
              )} />
            </>
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <PlaceholderImage width={400} height={225} />
            </div>
          )}

          {/* Icon Badge */}
          {iconName && (
            <div className={cn(
              "absolute top-3 left-3 bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm p-2 rounded-full shadow-sm transition-transform duration-300",
              isFocused ? "scale-110" : "scale-100"
            )}>
              <Icon
                icon={getIconId(iconName)}
                className="h-5 w-5 text-slate-900 dark:text-slate-200"
              />
            </div>
          )}

          {/* Title overlay for better readability */}
          <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-slate-900/90 to-transparent">
            <h3 className={cn(
              "text-lg font-semibold text-white line-clamp-1 transition-all duration-300",
              isFocused ? "translate-y-[-4px]" : ""
            )}>{translationKey ? t(`Projects.items.${translationKey}.title`) : title}</h3>
          </div>

          {/* Focus overlay */}
          <div className={cn(
            "absolute inset-0 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm transition-opacity duration-300",
            isFocused ? "opacity-100" : "opacity-0 pointer-events-none"
          )}>
            <span className="text-white font-medium px-4 py-2 rounded-full bg-slate-800/80 backdrop-blur-sm">
              {t('Projects.viewProject')}
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="flex flex-col flex-grow p-5 space-y-3">
          <p className={cn(
            "text-slate-600 dark:text-slate-400 text-sm line-clamp-3 flex-grow transition-colors duration-300",
            isFocused ? "text-slate-700 dark:text-slate-300" : ""
          )}>
            {translationKey ? t(`Projects.items.${translationKey}.description`) : description}
          </p>

          {/* Technologies */}
          {technologies.length > 0 && (
            <TechnologyTagList
              technologies={technologies}
              size="sm"
              variant="minimal"
              className="pt-2"
            />
          )}

          {/* Link-like Span */}
          <span
            className={cn(
              "inline-flex items-center gap-1.5 text-sm mt-2 transition-all duration-300",
              isFocused
                ? "text-slate-800 dark:text-slate-200 underline translate-x-1"
                : "text-slate-700 dark:text-slate-300 group-hover:text-slate-800 dark:group-hover:text-slate-200"
            )}
          >
            {t('Projects.viewProject')} <Icon
              icon="carbon:arrow-right"
              className={cn(
                "h-3 w-3 transition-transform duration-300",
                isFocused ? "translate-x-1" : "group-hover:translate-x-1"
              )}
            />
          </span>
        </div>
      </div>
    </Link>
  );
} 